import { useQuery } from '@apollo/client/react';
import { formatDistanceToNow } from 'date-fns';
import { useMemo } from 'react';

import {
  ACTIVITY_MESSAGES_QUERY,
  type ActivityMessagesData,
  type ActivityMessagesVars
} from './queries/activityMessages';

import { Spinner } from '@/components/ui/spinner';
import { getInitials } from '@/utils/getInitials';

interface ActivityEvent {
  field: string;
  id: string;
  kind: ActivityKind;
  newValue: null | string;
  oldValue: null | string;
  when: Date | null;
  who: string;
}

type ActivityKind = 'create' | 'delete' | 'edit';

interface Props {
  id: string;
}

function getKind(
  oldValue: null | string,
  newValue: null | string
): ActivityKind | null {
  if (isFalseString(oldValue) && !isFalseString(newValue)) return 'create';
  if (!isFalseString(oldValue) && isFalseString(newValue)) return 'delete';
  if (!isFalseString(oldValue) && !isFalseString(newValue)) return 'edit';
  return null;
}

function isFalseString(value: null | string): boolean {
  if (value === null) return true;
  const v = value.trim().toLowerCase();
  return v === '' || v === 'false';
}

function toValidDate(value: null | string): Date | null {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

const chipClass = 'rounded-xs bg-blue-100 px-0.5 text-xs text-blue-500';

export default function ActivityTab({ id }: Props) {
  const { data, error, loading } = useQuery<
    ActivityMessagesData,
    ActivityMessagesVars
  >(ACTIVITY_MESSAGES_QUERY, { skip: !id, variables: { recordId: id } });

  const events = useMemo<ActivityEvent[]>(() => {
    const out: ActivityEvent[] = [];

    for (const msg of data?.mailMessages ?? []) {
      for (const tv of msg.trackingValues ?? []) {
        const kind = getKind(tv.oldValue, tv.newValue);
        if (!kind) continue;

        const field =
          tv.fieldDescription?.trim() || tv.fieldName?.trim() || 'Field';

        const who = (msg.authorName || tv.createUserName || 'Unknown').trim();
        const when = toValidDate(tv.createDate) ?? toValidDate(msg.createDate);

        out.push({
          field,
          id: `${msg.id}:${tv.fieldId?.id ?? field}:${tv.createDate ?? ''}:${tv.oldValue ?? ''}:${tv.newValue ?? ''}`,
          kind,
          newValue: tv.newValue,
          oldValue: tv.oldValue,
          when,
          who
        });
      }
    }

    return out.sort(
      (a, b) => (b.when?.getTime() ?? 0) - (a.when?.getTime() ?? 0)
    );
  }, [data?.mailMessages]);

  if (loading) {
    return (
      <div className="grid h-40 place-content-center">
        <Spinner className="size-8 text-blue-500" />
      </div>
    );
  }

  if (error) return <div>{'Error'}</div>;

  if (events.length === 0) {
    return (
      <div className="text-muted-foreground pt-3 text-sm">
        {'No activity yet.'}
      </div>
    );
  }

  return (
    <div className="relative space-y-2.5 pt-3">
      <div className="bg-border absolute top-3 bottom-0 left-3 w-0.25" />
      {events.map(event => (
        <div
          className="flex items-center gap-x-2"
          key={event.id}
        >
          <div className="outline-background z-1 grid size-6 shrink-0 place-content-center self-start rounded-full bg-blue-200 text-sm text-blue-700 outline-2">
            {getInitials(event.who)}
          </div>
          <div className="flex flex-wrap items-center gap-1">
            <div className="text-sm font-medium">{event.who}</div>

            {event.kind === 'create' && (
              <>
                <div className="text-muted-foreground text-sm">{'created'}</div>
                <div className={chipClass}>{event.field}</div>
                {!isFalseString(event.newValue) && (
                  <>
                    <div className="text-muted-foreground text-sm">{'to'}</div>
                    <div
                      className={chipClass}
                      // eslint-disable-next-line react-dom/no-dangerously-set-innerhtml
                      dangerouslySetInnerHTML={{
                        __html: event.newValue ?? ''
                      }}
                    />
                  </>
                )}
              </>
            )}

            {event.kind === 'edit' && (
              <>
                <div className="text-muted-foreground text-sm">{'edited'}</div>
                <div className={chipClass}>{event.field}</div>
                {!isFalseString(event.oldValue) && (
                  <>
                    <div className="text-muted-foreground text-sm">
                      {'from'}
                    </div>
                    <div
                      className={chipClass}
                      // eslint-disable-next-line react-dom/no-dangerously-set-innerhtml
                      dangerouslySetInnerHTML={{
                        __html: event.oldValue ?? ''
                      }}
                    />
                  </>
                )}
                {!isFalseString(event.newValue) && (
                  <>
                    <div className="text-muted-foreground text-sm">{'to'}</div>
                    <div
                      className={chipClass}
                      // eslint-disable-next-line react-dom/no-dangerously-set-innerhtml
                      dangerouslySetInnerHTML={{
                        __html: event.newValue ?? ''
                      }}
                    />
                  </>
                )}
              </>
            )}

            {event.kind === 'delete' && (
              <>
                <div className="text-muted-foreground text-sm">{'deleted'}</div>
                <div className={chipClass}>{event.field}</div>
                {!isFalseString(event.oldValue) && (
                  <>
                    <div className="text-muted-foreground text-sm">
                      {'from'}
                    </div>
                    <div
                      className={chipClass}
                      // eslint-disable-next-line react-dom/no-dangerously-set-innerhtml
                      dangerouslySetInnerHTML={{
                        __html: event.oldValue ?? ''
                      }}
                    />
                  </>
                )}
              </>
            )}
          </div>

          {event.when && (
            <div className="text-muted-foreground ml-auto shrink-0 text-xs">
              {formatDistanceToNow(event.when, { addSuffix: true })}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
